import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Shield,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Smartphone,
  Key,
  QrCode
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
  withCredentials: true
});

// Add Authorization header from localStorage token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('hireun_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AdminSettings = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, has_pending_setup: false });
  
  // 2FA Setup state
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  
  // 2FA Disable state
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [isDisabling, setIsDisabling] = useState(false);

  useEffect(() => {
    load2FAStatus();
  }, []);

  const load2FAStatus = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/admin/2fa/status");
      setTwoFAStatus(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/app");
      } else {
        toast.error("Failed to load 2FA status");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startSetup = async () => {
    setIsSettingUp(true);
    try {
      const response = await apiClient.post("/admin/2fa/setup");
      setSetupData(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start 2FA setup");
    } finally {
      setIsSettingUp(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setIsVerifying(true);
    try {
      await apiClient.post("/admin/2fa/verify", { code: verificationCode });
      toast.success("2FA enabled successfully!");
      setShowSetupDialog(false);
      setSetupData(null);
      setVerificationCode("");
      load2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setIsVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setIsDisabling(true);
    try {
      await apiClient.post("/admin/2fa/disable", { code: disableCode });
      toast.success("2FA disabled");
      setShowDisableDialog(false);
      setDisableCode("");
      load2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setIsDisabling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Admin Security Settings
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 2FA Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your admin account
                </CardDescription>
              </div>
              <Badge variant={twoFAStatus.enabled ? "default" : "secondary"}>
                {twoFAStatus.enabled ? (
                  <><CheckCircle className="w-3 h-3 mr-1" /> Enabled</>
                ) : (
                  <><AlertCircle className="w-3 h-3 mr-1" /> Disabled</>
                )}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
              <Smartphone className="w-8 h-8 text-primary mt-1" />
              <div>
                <h4 className="font-medium">Authenticator App</h4>
                <p className="text-sm text-muted-foreground">
                  Use an authenticator app like Google Authenticator, Authy, or 1Password to generate one-time codes when signing in.
                </p>
              </div>
            </div>
            
            {twoFAStatus.enabled ? (
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium">2FA is active</p>
                    <p className="text-sm text-muted-foreground">Your account is protected with two-factor authentication</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setShowDisableDialog(true)}
                  data-testid="disable-2fa-btn"
                >
                  Disable 2FA
                </Button>
              </div>
            ) : (
              <Button 
                onClick={startSetup} 
                disabled={isSettingUp}
                className="w-full"
                data-testid="setup-2fa-btn"
              >
                {isSettingUp ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <QrCode className="w-4 h-4 mr-2" />
                )}
                Set Up Two-Factor Authentication
              </Button>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Set Up 2FA
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4 py-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <img 
                  src={setupData.qr_code} 
                  alt="2FA QR Code" 
                  className="w-48 h-48 border rounded-lg"
                  data-testid="2fa-qr-code"
                />
              </div>
              
              {/* Manual Entry */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Or enter this code manually:</p>
                <code className="px-3 py-2 bg-secondary rounded text-sm font-mono break-all">
                  {setupData.secret}
                </code>
              </div>
              
              {/* Verification */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Enter verification code</label>
                <Input
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-lg tracking-widest font-mono"
                  data-testid="2fa-verify-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={verifySetup} 
              disabled={isVerifying || verificationCode.length !== 6}
              data-testid="verify-2fa-btn"
            >
              {isVerifying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              Disable 2FA
            </DialogTitle>
            <DialogDescription>
              Enter your current 2FA code to disable two-factor authentication
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                Warning: Disabling 2FA will make your admin account less secure. Only do this if you've lost access to your authenticator app.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Current 2FA code</label>
              <Input
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg tracking-widest font-mono"
                data-testid="disable-2fa-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={disable2FA} 
              disabled={isDisabling || disableCode.length !== 6}
              data-testid="confirm-disable-2fa-btn"
            >
              {isDisabling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
